import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  MapPin, Globe2, Calendar, Link as LinkIcon, 
  Briefcase, GraduationCap, Award, Shield,
  Users, FileText, Video, MessageSquare, Star
} from 'lucide-react';
import { demoUsers } from '../data/demoUsers';
import { demoPosts, demoArticles, demoVideos } from '../data/demoData';
import ContentGrid from '../components/common/ContentGrid';
import Card from '../components/common/Card';
import { useShare } from '../hooks/useShare';

type ContentTab = 'posts' | 'articles' | 'videos' | 'about';

export default function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const handleShare = useShare();
  const [activeTab, setActiveTab] = useState<ContentTab>('posts');

  // Find user
  const user = demoUsers.find(u => u.id === id);
  if (!user) return null;

  // Get user's content
  const userContent = {
    posts: demoPosts.filter(post => post.author.id === id),
    articles: demoArticles.filter(article => article.author.id === id),
    videos: demoVideos.filter(video => video.author.id === id)
  };

  const stats = [
    { label: 'Posts', value: userContent.posts.length, icon: <MessageSquare className="h-4 w-4" /> },
    { label: 'Articles', value: userContent.articles.length, icon: <FileText className="h-4 w-4" /> },
    { label: 'Videos', value: userContent.videos.length, icon: <Video className="h-4 w-4" /> },
    { label: 'Followers', value: '3.2K', icon: <Users className="h-4 w-4" /> },
    { label: 'Following', value: '892', icon: <Star className="h-4 w-4" /> }
  ];

  const tabs = [
    { id: 'posts', label: 'Posts', count: userContent.posts.length },
    { id: 'articles', label: 'Articles', count: userContent.articles.length },
    { id: 'videos', label: 'Videos', count: userContent.videos.length },
    { id: 'about', label: 'About', count: null }
  ];

  const getCurrentContent = () => {
    switch (activeTab) {
      case 'posts':
        return userContent.posts;
      case 'articles':
        return userContent.articles;
      case 'videos':
        return userContent.videos;
      default:
        return [];
    }
  };

  return (
    <div className="space-y-6">
      {/* Cover Image */}
      <div className="relative h-48 md:h-64 rounded-xl overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1557683316-973673baf926?w=1200"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 ${
          theme === 'dark' ? 'bg-gradient-to-t from-gray-900' : 'bg-gradient-to-t from-white'
        }`} />
      </div>

      {/* Profile Info */}
      <div className="relative px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-5">
          <div className="flex">
            <div className="-mt-20 relative">
              <img
                src={user.avatar}
                alt={user.username}
                className="h-32 w-32 rounded-xl ring-4 ring-blue-500/20 object-cover"
              />
              <div className={`
                absolute bottom-2 right-2 h-5 w-5 rounded-full ring-2
                ${theme === 'dark' ? 'bg-green-500 ring-gray-900' : 'bg-green-500 ring-white'}
              `} />
            </div>
          </div>

          <div className="mt-6 sm:mt-0 min-w-0 flex-1">
            <div className="flex items-center space-x-3">
              <h1 className={`text-2xl font-bold truncate ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {user.name} {user.surname}
              </h1>
              <Shield className="h-5 w-5 text-blue-500" />
            </div>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              @{user.username}
            </p>
          </div>

          <div className="mt-6 sm:mt-0 flex flex-col sm:flex-row sm:items-center sm:space-x-3">
            <button className={`
              px-4 py-2 rounded-lg transition-colors
              ${theme === 'dark'
                ? 'bg-gray-800 text-gray-100 hover:bg-gray-700'
                : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
              }
            `}>
              Edit Profile
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Follow
            </button>
          </div>
        </div>

        {/* Bio & Info */}
        <div className="mt-6 max-w-4xl">
          <p className={`text-sm ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {user.bio || "Senior Software Engineer passionate about AI and machine learning. Building the future of technology through innovative solutions."}
          </p>

          <div className="mt-4 flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Briefcase className={`h-4 w-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {user.role}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className={`h-4 w-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {user.region.city}, {user.region.country}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe2 className={`h-4 w-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {user.languages?.join(', ')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className={`h-4 w-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Joined March 2023
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {stat.label}
                </span>
                {stat.icon}
              </div>
              <div className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {stat.value}
              </div>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ContentTab)}
                className={`
                  pb-4 relative
                  ${activeTab === tab.id
                    ? theme === 'dark'
                      ? 'text-blue-400'
                      : 'text-blue-600'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-gray-300'
                      : 'text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                <span className="font-medium">
                  {tab.label}
                  {tab.count !== null && (
                    <span className={`ml-2 text-sm ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </span>
                {activeTab === tab.id && (
                  <div className={`
                    absolute bottom-0 left-0 right-0 h-0.5 rounded-full
                    ${theme === 'dark' ? 'bg-blue-400' : 'bg-blue-600'}
                  `} />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {activeTab === 'about' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Experience */}
              <Card className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Briefcase className="h-5 w-5 text-blue-500" />
                  <h2 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    Experience
                  </h2>
                </div>
                <div className="space-y-4">
                  {[
                    {
                      role: 'Senior Software Engineer',
                      company: 'TechCorp',
                      period: '2021 - Present'
                    },
                    {
                      role: 'Software Engineer',
                      company: 'InnovateTech',
                      period: '2019 - 2021'
                    }
                  ].map((exp, index) => (
                    <div key={index} className="flex justify-between items-start">
                      <div>
                        <p className={`font-medium ${
                          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                        }`}>
                          {exp.role}
                        </p>
                        <p className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {exp.company}
                        </p>
                      </div>
                      <span className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {exp.period}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Education */}
              <Card className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <GraduationCap className="h-5 w-5 text-green-500" />
                  <h2 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    Education
                  </h2>
                </div>
                <div className="space-y-4">
                  {[
                    {
                      degree: 'MSc Computer Science',
                      school: 'Tech University',
                      period: '2017 - 2019'
                    },
                    {
                      degree: 'BSc Computer Engineering',
                      school: 'State University',
                      period: '2013 - 2017'
                    }
                  ].map((edu, index) => (
                    <div key={index} className="flex justify-between items-start">
                      <div>
                        <p className={`font-medium ${
                          theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                        }`}>
                          {edu.degree}
                        </p>
                        <p className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {edu.school}
                        </p>
                      </div>
                      <span className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {edu.period}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Skills */}
              <Card className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Award className="h-5 w-5 text-purple-500" />
                  <h2 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    Skills
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {user.expertise?.map((skill, index) => (
                    <span
                      key={index}
                      className={`
                        px-3 py-1 rounded-full text-sm
                        ${theme === 'dark'
                          ? 'bg-gray-700 text-gray-300'
                          : 'bg-gray-100 text-gray-700'
                        }
                      `}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </Card>

              {/* Interests */}
              <Card className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Star className="h-5 w-5 text-yellow-500" />
                  <h2 className={`text-lg font-semibold ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}>
                    Interests
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {user.interests?.map((interest, index) => (
                    <span
                      key={index}
                      className={`
                        px-3 py-1 rounded-full text-sm
                        ${theme === 'dark'
                          ? 'bg-gray-700 text-gray-300'
                          : 'bg-gray-100 text-gray-700'
                        }
                      `}
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </Card>
            </div>
          ) : (
            <ContentGrid
              items={getCurrentContent()}
              onLike={(id) => {
                console.log('Liked content:', id);
              }}
              onShare={async (id) => {
                const content = getCurrentContent().find(item => item.id === id);
                if (content) {
                  await handleShare(content);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}